// src/pages/Privacy.jsx

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--fg);

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: var(--fg);
  opacity: 0.7;
  margin-bottom: 40px;
`;

const Content = styled.div`
  background: var(--card);
  border: 1px solid var(--bdr);
  border-radius: 16px;
  padding: 40px;
  box-shadow: var(--shadow);
  line-height: 1.8;

  @media (max-width: 768px) {
    padding: 24px;
  }

  h2 {
    font-size: 24px;
    font-weight: 700;
    margin: 32px 0 16px;
    color: var(--fg);

    &:first-child {
      margin-top: 0;
    }
  }

  h3 {
    font-size: 20px;
    font-weight: 600;
    margin: 24px 0 12px;
    color: var(--fg);
  }

  p {
    margin: 0 0 16px;
    color: var(--fg);
  }

  ul, ol {
    margin: 0 0 16px;
    padding-left: 24px;
    color: var(--fg);
  }

  li {
    margin-bottom: 8px;
  }

  strong {
    font-weight: 600;
    color: var(--fg);
  }

  a {
    color: var(--primary);
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
`;

export default function Privacy() {
  const { t, i18n } = useTranslation();
  const isRU = i18n.language === 'ru';

  // Устанавливаем title страницы
  React.useEffect(() => {
    document.title = isRU 
      ? 'Политика конфиденциальности | АСТОРИУС' 
      : 'Privacy Policy | ASTORIUS';
  }, [isRU]);

  return (
    <>
      <Container>
        <Title>
          {isRU ? 'Политика конфиденциальности' : 'Privacy Policy'}
        </Title>
        <Subtitle>
          {isRU ? 'Последнее обновление: 6 марта 2026' : 'Last updated: March 6, 2026'}
        </Subtitle>

        <Content>
          {isRU ? (
            <>
              <h2>Кто мы</h2>
              <p>
                Корпорация «АСТОРИУС»<br/>
                Наш адрес сайта: https://www.astorius-elite.ru/
              </p>

              <h2>Комментарии</h2>
              <p>
                Если посетитель оставляет комментарий на сайте, мы собираем данные, указанные в форме комментария, 
                а также IP адрес посетителя и данные user-agent браузера с целью определения спама.
              </p>
              <p>
                Анонимизированная строка создаваемая из вашего адреса email («хеш») может предоставляться сервису Gravatar, 
                чтобы определить используете ли вы его. Политика конфиденциальности Gravatar доступна здесь: 
                <a href="https://automattic.com/privacy/" target="_blank" rel="noopener noreferrer">https://automattic.com/privacy/</a>. 
                После одобрения комментария ваше изображение профиля будет видимым публично в контексте вашего комментария.
              </p>

              <h2>Медиафайлы</h2>
              <p>
                Если вы зарегистрированный пользователь и загружаете фотографии на сайт, вам возможно следует избегать 
                загрузки изображений с метаданными EXIF, так как они могут содержать данные вашего месторасположения по GPS. 
                Посетители могут извлечь эту информацию скачав изображения с сайта.
              </p>

              <h2>Куки</h2>
              <p>
                Если вы оставляете комментарий на нашем сайте, вы можете включить сохранение вашего имени, адреса email 
                и вебсайта в куки. Это делается для вашего удобства, чтобы не заполнять данные снова при повторном 
                комментировании. Эти куки хранятся в течение одного года.
              </p>
              <p>
                Если у вас есть учетная запись на сайте и вы войдете в неё, мы установим временный куки для определения 
                поддержки куки вашим браузером, куки не содержит никакой личной информации и удаляется при закрытии 
                вашего браузера.
              </p>
              <p>
                При входе в учетную запись мы также устанавливаем несколько куки с данными входа и настройками экрана. 
                Куки входа хранятся в течение двух дней, куки с настройками экрана — год. Если вы выберете возможность 
                «Запомнить меня», данные о входе будут сохраняться в течение двух недель. При выходе из учетной записи 
                куки входа будут удалены.
              </p>
              <p>
                При редактировании или публикации статьи в браузере будет сохранен дополнительный куки, он не содержит 
                персональных данных и содержит только ID записи отредактированной вами, истекает через 1 день.
              </p>

              <h2>Встраиваемое содержимое других вебсайтов</h2>
              <p>
                Статьи на этом сайте могут включать встраиваемое содержимое (например видео, изображения, статьи и др.), 
                подобное содержимое ведет себя так же, как если бы посетитель зашел на другой сайт.
              </p>
              <p>
                Эти сайты могут собирать данные о вас, использовать куки, внедрять дополнительное отслеживание третьей 
                стороной и следить за вашим взаимодействием с внедренным содержимым, включая отслеживание взаимодействия, 
                если у вас есть учетная запись и вы авторизовались на том сайте.
              </p>

              <h2>С кем мы делимся вашими данными</h2>
              <p>
                Указанные Вами на сайте телефон и email мы используем в деловой переписке с корпорацией «АСТОРИУС».
              </p>
              <p>
                А также на эти контактные данные отправляется информация новостного или рекламного характера от 
                корпорации «АСТОРИУС».
              </p>
              <p>
                Если вы запросите сброс пароля, ваш IP будет указан в email-сообщении о сбросе.
              </p>

              <h2>Как долго мы храним ваши данные</h2>
              <p>
                Если вы оставляете комментарий, то сам комментарий и его метаданные сохраняются неопределенно долго. 
                Это делается для того, чтобы определять и одобрять последующие комментарии автоматически, вместо 
                помещения их в очередь на одобрение.
              </p>
              <p>
                Для пользователей с регистрацией на нашем сайте мы храним ту личную информацию, которую они указывают 
                в своем профиле. Все пользователи могут видеть, редактировать или удалить свою информацию из профиля 
                в любое время (кроме имени пользователя). Администрация вебсайта также может видеть и изменять эту информацию.
              </p>
              <p>
                Телефон и email хранятся в нашей базе данных бессрочно долго.
              </p>

              <h2>Какие у вас права на ваши данные</h2>
              <p>
                При наличии учетной записи на сайте или если вы оставляли комментарии, то вы можете запросить файл 
                экспорта персональных данных, которые мы сохранили о вас, включая предоставленные вами данные. 
                Вы также можете запросить удаление этих данных, это не включает данные, которые мы обязаны хранить 
                в административных целях, по закону или целях безопасности.
              </p>

              <h2>Куда мы отправляем ваши данные</h2>
              <p>
                Комментарии пользователей могут проверяться автоматическим сервисом определения спама.
              </p>
              <p>
                Телефон и email могут предоставляться специализированным ресурсам по информированию Вас о текущих 
                мероприятиях корпорация «АСТОРИУС».
              </p>
            </>
          ) : (
            <>
              <h2>1. General Provisions</h2>
              <p>
                This Privacy Policy defines the procedure for processing and protecting personal data of 
                ASTORIUS website users (hereinafter referred to as the "Website"). By using the Website, 
                you agree to the terms of this Policy.
              </p>

              <h2>2. Information We Collect</h2>
              <p>We may collect the following types of information:</p>
              <ul>
                <li><strong>Personal Information:</strong> name, surname, email address, phone number that you provide when filling out feedback forms or registering.</li>
                <li><strong>Technical Information:</strong> IP address, browser type, operating system, pages you visit, visit time.</li>
                <li><strong>Cookies:</strong> small text files that are saved on your device to improve the Website's performance.</li>
              </ul>

              <h2>3. Purposes of Information Use</h2>
              <p>The collected information is used for:</p>
              <ul>
                <li>Providing services and processing requests</li>
                <li>Improving the quality of the Website</li>
                <li>Personalizing content and advertising</li>
                <li>Analyzing Website usage</li>
                <li>Contacting you regarding real estate matters</li>
                <li>Complying with legal requirements</li>
              </ul>

              <h2>4. Cookies</h2>
              <p>
                We use cookies to improve the Website's performance, analyze traffic, and personalize content. 
                You can manage cookie settings in your browser, however disabling some cookies may limit 
                the Website's functionality.
              </p>
              <p>Types of cookies used:</p>
              <ul>
                <li><strong>Necessary cookies:</strong> provide basic Website functionality</li>
                <li><strong>Analytical cookies:</strong> help understand how visitors use the Website</li>
                <li><strong>Functional cookies:</strong> remember your preferences (language, currency)</li>
              </ul>

              <h2>5. Data Transfer to Third Parties</h2>
              <p>
                We do not sell or transfer your personal data to third parties without your consent, 
                except as required by law. We may transfer data to:
              </p>
              <ul>
                <li>Partners who help us provide services (with confidentiality compliance)</li>
                <li>Government authorities upon their lawful request</li>
                <li>In case of business reorganization or sale</li>
              </ul>

              <h2>6. Data Protection</h2>
              <p>
                We apply technical and organizational measures to protect your personal data from 
                unauthorized access, modification, disclosure, or destruction. However, no method of 
                data transmission over the Internet is absolutely secure.
              </p>

              <h2>7. Your Rights</h2>
              <p>You have the right to:</p>
              <ul>
                <li>Receive information about what data we store about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your personal data</li>
                <li>Withdraw consent to data processing</li>
                <li>Restrict processing of your data</li>
                <li>Receive a copy of your data in a structured format</li>
              </ul>

              <h2>8. Data Retention</h2>
              <p>
                We store your personal data only for the period necessary to achieve the purposes 
                specified in this Policy, or in accordance with legal requirements.
              </p>

              <h2>9. Changes to the Policy</h2>
              <p>
                We reserve the right to make changes to this Privacy Policy. The updated version 
                will be published on this page with the date of the last update indicated.
              </p>

              <h2>10. Contact Information</h2>
              <p>
                If you have questions about this Privacy Policy or want to exercise your rights, 
                contact us:
              </p>
              <ul>
                <li><strong>Email:</strong> info@astorius.ae</li>
                <li><strong>Phone:</strong> +7 (495) 972-97-97</li>
                <li><strong>Address:</strong> Moscow, Bakhrushina St., 23 bldg. 1</li>
              </ul>
            </>
          )}
        </Content>
      </Container>
    </>
  );
}
